import {
  Button,
  Card,
  DataStatus,
  Dialog,
  Flag,
  showNotification,
  Switch,
} from 'platform/components';
import {HStack, Link, Right, Text, VStack} from 'platform/foundation';

import {Trans} from 'react-i18next';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useDeleteFacebookAuthorizationMutation,
  useGetMetaAuthorizationQuery,
  useGetTenantQuery,
  useLazyGenerateFacebookLoginUrlQuery,
  usePatchFacebookAuthorizationMutation,
  usePostFacebookCallbackMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {useBoolean, useOnMount, useQueryState} from 'shared/web';

export function FacebookCredentialsSettings() {
  const [code] = useQueryState('code');
  const [state] = useQueryState('state');
  const [error] = useQueryState('error');

  const {data: tenant, isLoading: isGetTenantLoading} = useGetTenantQuery();

  const {data: metaAuthorization, isLoading: isGetMetaAuthorizationLoading} =
    useGetMetaAuthorizationQuery(
      {
        'X-Tenant': tenant?.id ?? '',
      },
      {skip: isNilOrEmpty(tenant)}
    );

  const [generateFacebookLoginUrl, {isLoading: isGetFacebookLoginUrlLoading}] =
    useLazyGenerateFacebookLoginUrlQuery();

  const [postFacebookCallback, {isLoading: isPostFacebookCallbackLoading}] =
    usePostFacebookCallbackMutation();

  const [deleteFacebookAuthorization, {isLoading: isDeleteFacebookAuthorizationLoading}] =
    useDeleteFacebookAuthorizationMutation();

  const [patchFacebookAuthorization] = usePatchFacebookAuthorizationMutation();

  useOnMount(() => {
    if ((code || state || error) && tenant?.id) {
      postFacebookCallback({
        code,
        state,
        error,
        'X-Tenant': tenant.id,
      })
        .unwrap()
        .then((response) => {
          if (response.errorMessage) {
            showNotification.error(response.errorMessage);
          } else {
            showNotification.success(
              i18n.t('page.settings.facebookCredentials.authorizationSuccess')
            );
          }
        })
        .catch(handleApiError);
    }
  });

  const handleAuthorize = () => {
    if (!tenant?.id) {
      return;
    }

    generateFacebookLoginUrl({
      'X-Tenant': tenant.id,
    })
      .unwrap()
      .then((response) => {
        if (response.url) {
          window.location.href = response.url;
        } else {
          showNotification.error(i18n.t('page.settings.facebookCredentials.noUrlReturned'));
        }
      })
      .catch(handleApiError);
  };

  const [isRevokeDialogOpen, openRevokeDialog, closeRevokeDialog] = useBoolean(false);

  const handleRevokeConfirm = () => {
    if (!tenant?.id) {
      return;
    }

    deleteFacebookAuthorization({
      'X-Tenant': tenant.id,
    })
      .unwrap()
      .then(() => {
        closeRevokeDialog();
        showNotification.success(i18n.t('page.settings.facebookCredentials.authorizationDeleted'));
      })
      .catch(handleApiError);
  };

  const handleEnableChange = (value: boolean) => {
    if (!tenant?.id) {
      return;
    }

    patchFacebookAuthorization({
      patchFacebookAuthorizationRequestBody: {
        enable: value,
      },
      'X-Tenant': tenant.id,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  const isLoading =
    isGetTenantLoading || isGetMetaAuthorizationLoading || isPostFacebookCallbackLoading;

  const isAuthorized = !!metaAuthorization?.authorized;
  const isEnabled = !!metaAuthorization?.enabled;

  const switchLabel = isEnabled
    ? i18n.t('page.settings.facebookCredentials.enabled')
    : i18n.t('page.settings.facebookCredentials.disabled');

  return (
    <DataStatus isLoading={isLoading}>
      <VStack spacing={6}>
        <Text size="small">
          <Trans
            i18nKey="page.settings.facebookCredentials.description2"
            components={{
              doclink: (
                <Link
                  size="small"
                  href={i18n.t('page.settings.facebookCredentials.doclink')}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </Text>

        <Card
          variant="inlineGrey"
          title={i18n.t('page.settings.facebookCredentials.title')}
          data-testid="facebook-credentials-card"
        >
          <VStack spacing={4}>
            <Switch
              label={switchLabel}
              value={isEnabled}
              onChange={handleEnableChange}
              isDisabled={!isAuthorized}
              data-testid="facebook-credentials-enabled-switch"
            />

            <HStack spacing={2} align="center">
              <Text size="small" color="secondary">
                {i18n.t('page.settings.facebookCredentials.authorizationStatus')}:
              </Text>
              <Flag
                label={
                  isAuthorized
                    ? i18n.t('page.settings.facebookCredentials.authorized')
                    : i18n.t('page.settings.facebookCredentials.notAuthorized')
                }
                colorScheme={isAuthorized ? 'green' : 'red'}
                isSubtle
                data-testid="facebook-credentials-status-flag"
              />
            </HStack>
          </VStack>
        </Card>

        <Right>
          <HStack spacing={3}>
            <Button
              variant="dangerOutlined"
              onClick={openRevokeDialog}
              isDisabled={!isAuthorized || isDeleteFacebookAuthorizationLoading}
              isLoading={isDeleteFacebookAuthorizationLoading}
              title={i18n.t('page.settings.facebookCredentials.revokeAuthorization')}
              data-testid="facebook-credentials-revoke-button"
            />
            <Button
              variant="primary"
              onClick={handleAuthorize}
              isDisabled={isAuthorized || isGetFacebookLoginUrlLoading}
              isLoading={isGetFacebookLoginUrlLoading}
              title={i18n.t('page.settings.facebookCredentials.authorize')}
              data-testid="facebook-credentials-authorize-button"
            />
          </HStack>
        </Right>
      </VStack>

      <Dialog
        isOpen={isRevokeDialogOpen}
        onClose={closeRevokeDialog}
        size="small"
        buttons={[
          {
            variant: 'secondary',
            onClick: closeRevokeDialog,
            isDisabled: isDeleteFacebookAuthorizationLoading,
            title: i18n.t('general.actions.cancel'),
          },
          {
            variant: 'danger',
            onClick: handleRevokeConfirm,
            isLoading: isDeleteFacebookAuthorizationLoading,
            title: i18n.t('page.settings.facebookCredentials.revokeAuthorization'),
          },
        ]}
      >
        <Text>{i18n.t('page.settings.facebookCredentials.deleteAuthorizationText')}</Text>
      </Dialog>
    </DataStatus>
  );
}
